'use client';

import { LayoutGroup, AnimatePresence } from 'framer-motion';
import { Suspense, useEffect, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { createPortal } from 'react-dom';
import { ToastContainer } from 'react-toastify';
import ClientLayoutWrapper from './ClientLayoutWrapper';
import ErrorBoundary from '@/components/ErrorBoundary';
import Footer from '@/components/Footer';
import {
  SidebarLayoutProvider,
  useSidebarLayout,
} from '@/contexts/SidebarLayoutContext';
import { isCloneProfilePath } from '@/lib/clone-profile-path';

function shouldHideFooter(pathname: string | null): boolean {
  return (
    isCloneProfilePath(pathname) ||
    !!pathname?.startsWith('/create-clone') ||
    !!pathname?.startsWith('/dashboard') ||
    pathname === '/jobs' ||
    pathname === '/career-roadmap' ||
    pathname === '/courses' ||
    pathname === '/capabilities' ||
    pathname === '/refer' ||
    !!pathname?.startsWith('/clone-settings') ||
    pathname === '/clone-activity'
  );
}

function sidebarFooterOffsetClass(
  hideFooter: boolean,
  hasSidebar: boolean,
  isExpanded: boolean
): string {
  if (!hideFooter || !hasSidebar) return '';
  return isExpanded
    ? 'md:ml-64 md:w-[calc(100%-16rem)] transition-all duration-300'
    : 'md:ml-16 md:w-[calc(100%-4rem)] transition-all duration-300';
}

function AppFooter({
  pathname,
  hideFooter,
  sidebarFooterOffset,
}: {
  pathname: string | null;
  hideFooter: boolean;
  sidebarFooterOffset: string;
}) {
  const searchParams = useSearchParams();
  const isTalentEmbedVideomode =
    isCloneProfilePath(pathname) && searchParams.get('videomode') === 'true';

  const footerClassName = isTalentEmbedVideomode
    ? 'hidden'
    : hideFooter
      ? `hidden md:grid ${sidebarFooterOffset}`.trim()
      : '';

  return <Footer className={footerClassName} />;
}

function ClientRootInner({ children }: { children: React.ReactNode }) {
  const [mounted, setMounted] = useState(false);
  const pathname = usePathname();
  const { hasSidebar, isExpanded } = useSidebarLayout();

  useEffect(() => {
    setMounted(true);
  }, []);

  // Hide the global footer on full-height app pages to prevent it from
  // sitting above the keyboard on mobile.
  const hideFooter = shouldHideFooter(pathname);
  const sidebarFooterOffset = sidebarFooterOffsetClass(hideFooter, hasSidebar, isExpanded);

  // On clone profile paths, keep the footer fully hidden until search params
  // resolve so a videomode embed never flashes a footer.
  const footerFallbackClassName = isCloneProfilePath(pathname)
    ? 'hidden'
    : hideFooter
      ? `hidden md:grid ${sidebarFooterOffset}`.trim()
      : '';

  return (
    <>
      {mounted &&
        createPortal(<ToastContainer position="top-center" autoClose={10000} />, document.body)}

      <div className="relative z-10 flex flex-col h-screen overflow-hidden">
        <ClientLayoutWrapper />

        <LayoutGroup>
          <AnimatePresence mode="wait">
            <main className="relative flex-1 overflow-hidden">
              <ErrorBoundary>{children}</ErrorBoundary>
            </main>
          </AnimatePresence>
        </LayoutGroup>

        <Suspense fallback={<Footer className={footerFallbackClassName} />}>
          <AppFooter
            pathname={pathname}
            hideFooter={hideFooter}
            sidebarFooterOffset={sidebarFooterOffset}
          />
        </Suspense>
      </div>
    </>
  );
}

export default function ClientRoot({ children }: { children: React.ReactNode }) {
  return (
    <SidebarLayoutProvider>
      <ClientRootInner>{children}</ClientRootInner>
    </SidebarLayoutProvider>
  );
}
